import {
  ChatBubbleLeftIcon,
  PhoneIcon,
  XCircleIcon
} from '@heroicons/react/24/solid'
import { usePathname } from 'next/navigation'
import { CSSProperties, FC, useState } from 'react'

import { Premise } from '@/api/models/auth/lib/auth.types'
import { useContactInfo } from '@/api/models/common/hooks/useContactInfo'
import { RequestByIdApiType } from '@/api/models/requests'
import { RequestTypeAlias } from '@/api/models/requests/lib/requests.types'
import { YandexMetrikaEvent } from '@/app/constants/metrics'
import RepeatSvg from '@/assets/svg/Repeat.svg'
import { requestTypesWithComments } from '@/components/entities/requests/model/constants'
import { PageType } from '@/components/entities/requests/model/types'
import { ContentButton } from '@/components/entities/requests/ui/RequestDetails/Content/ContentButton'
import { useYandexMetrika } from '@/components/shared/yandex-metrika/hooks/useYandexMetrika'
import { useRequestNavigation } from '@/components/widgets/request/hooks/useRequestNavigation'
import { makePhoneCallable } from '@/utils/number'

import { CancelRequestModal } from '../CancelRequestModal/CancelRequestModal'
import ContentDefault from './ContentDefault'

type Props = {
  request: RequestByIdApiType
  pageType: PageType
  premises?: Premise[]
  style?: CSSProperties
  onCancelSuccess: () => void
}

const Content: FC<Props> = ({
  request,
  pageType,
  premises,
  style,
  onCancelSuccess
}) => {
  const { reachGoal } = useYandexMetrika()
  const pathname = usePathname()
  const { data: contactInfo } = useContactInfo()
  const hasContactPhone = Boolean(contactInfo?.contactPhone)

  const { navigateToRepeatRequest, navigateToCommentsFromDetails } =
    useRequestNavigation()

  const [isModalOpen, setIsModalOpen] = useState(false)

  const areCommentsVisible = requestTypesWithComments.includes(
    request.typeAlias
  )

  const isRentPassAuto = request.typeAlias === RequestTypeAlias.RentPassAuto

  const handleCallSupport = () => {
    if (!contactInfo?.contactPhone) {
      return
    }

    window.location.href = `tel:${makePhoneCallable(contactInfo.contactPhone)}`
  }

  const handleRefreshRequest = () => {
    if (pathname === '/') {
      reachGoal(
        request.typeAlias === RequestTypeAlias.CommonRequest
          ? YandexMetrikaEvent.MAIN_NEW_REQUESTS_REPLAY
          : YandexMetrikaEvent.MAIN_PASSES_REPLAY
      )
    } else {
      reachGoal(
        request.typeAlias === RequestTypeAlias.CommonRequest
          ? YandexMetrikaEvent.NEW_REQUEST_REPLAY
          : YandexMetrikaEvent.PASSES_REPLAY
      )
    }
    navigateToRepeatRequest(pageType, request.id, request.typeAlias)
  }

  const handleRedirectToComments = async () => {
    navigateToCommentsFromDetails(pageType, request.id)
  }

  return (
    <>
      <div style={style} className="-mt-14 flex-grow overflow-y-auto">
        <div className="rounded-16 bg-grey-10 p-24 xs:p-16">
          <ContentDefault request={request} premises={premises} />
        </div>
      </div>

      <div className="flex gap-10 pb-40 pt-24 sm:pb-32">
        {request.canBeRejected && !isRentPassAuto && (
          <ContentButton
            pageType={pageType}
            onClick={() => setIsModalOpen(true)}
            icon={<XCircleIcon className="h-24 w-24 text-grey-40" />}
            text="Отменить"
          />
        )}

        <ContentButton
          pageType={pageType}
          onClick={handleRefreshRequest}
          icon={<RepeatSvg className="h-24 w-24 text-grey-40" />}
          text="Повторить"
        />
        {areCommentsVisible && (
          <ContentButton
            pageType={pageType}
            onClick={handleRedirectToComments}
            icon={<ChatBubbleLeftIcon className="h-24 w-24 text-grey-40" />}
            badge={request.unreadComments}
            text="Чат"
            dataTestId="request-chat-button-id"
          />
        )}
        {isRentPassAuto && hasContactPhone && (
          <ContentButton
            pageType={pageType}
            onClick={handleCallSupport}
            icon={<PhoneIcon className="h-24 w-24 text-grey-40" />}
            text="Обратиться в поддержку"
          />
        )}
      </div>

      <CancelRequestModal
        pageType={pageType}
        requestType={request.typeAlias}
        requestStatus={request.statusAlias}
        requestNumber={request.number}
        requestId={request.id}
        visible={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCancelSuccess={onCancelSuccess}
      />
    </>
  )
}

export default Content
